import React from 'react';
import { useTable, useSortBy } from 'react-table';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import Button from 'react-bootstrap/Button';
import { ReactComponent as ChevronRightIcon } from '../../../shared/icons/chevron-right.svg';
import { ReactComponent as InfoIcon } from '../../../shared/icons/info-unfilled-icon.svg';
import { Table } from '../../../components/table';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import styles from './styles.module.scss';
import Tooltip from '../../../components/tooltip';
import { ReactComponent as GlobeIcon } from '../../../shared/icons/globe-icon.svg';
import { ReactComponent as HomeIcon } from '../../../shared/icons/home-icon.svg';
import { ReactComponent as FolderIcon } from '../../../shared/icons/folder-icon.svg';

interface DocumentTemplateModalTableProps {
  data: any[];
  selectTemplate: (templateId: string | number, teamId: string, teamName?: string) => void;
  selectedTemplateId: string | number;
  isSearching?: boolean;
  onFolderClick?: (folderId: string, folderName: string, isVirtual: boolean) => void;
  currentPath?: Array<{ id: string | null; name: string; isVirtual?: boolean }>;
  onNavigateBack?: () => void;
}

export const DocumentTemplateModalTable = ({
  data,
  selectTemplate,
  selectedTemplateId,
  isSearching = false,
  onFolderClick,
  currentPath = [{ id: null, name: 'Templates' }],
  onNavigateBack,
}: DocumentTemplateModalTableProps) => {
  // Add onRowClick handler to each row for full-row clickability
  const dataWithClickHandlers = React.useMemo(() => {
    return data.map((item) => ({
      ...item,
      onRowClick: () => {
        if (item.isVirtualFolder || item.isFolder) {
          // Open folder
          onFolderClick && onFolderClick(item.id, item.name, item.isVirtualFolder);
        } else {
          // Select template
          selectTemplate(item.id, item.team?.uuid, item.team?.name);
        }
      },
    }));
  }, [data, onFolderClick, selectTemplate]);

  const columns = React.useMemo(
    () => [
      {
        Header: <FormattedMessage id="lease.template.name" defaultMessage="Template Name" />,
        accessor: 'name',
        Cell: ({ row: { original } }: any) => {
          return (
            <div>
              <div className="d-flex align-items-center">
                {original.isVirtualFolder && (
                  <GlobeIcon className="text-primary mr-2" style={{ width: '16px', height: '16px' }} />
                )}
                {original.isFolder && !original.isVirtualFolder && (
                  <FolderIcon className="text-primary mr-2" style={{ width: '16px', height: '16px' }} />
                )}
                {original.description &&
                  original.description.length > 0 &&
                  !original.isVirtualFolder &&
                  !original.isFolder && (
                    <Tooltip
                      testId="lease-template-tooltip"
                      placement="bottom"
                      icon={<InfoIcon className="text-primary mr-1" />}
                    >
                      {original.description}
                    </Tooltip>
                  )}
                {original.name}
              </div>
              <div className="text-muted">
                {isSearching && original.folderPath ? (
                  <div>
                    <div>{original.team?.name}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>📁 {original.folderPath}</div>
                  </div>
                ) : (
                  original.team?.name
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: <FormattedMessage id="lease.template.type" defaultMessage="Type" />,
        accessor: 'docTemplateType',
        width: 120,
        Cell: ({ row: { original } }: any) => {
          if (original.isVirtualFolder && original.templateCount) {
            return (
              <span className="text-muted">
                {original.templateCount} {original.templateCount === 1 ? 'template' : 'templates'}
              </span>
            );
          }
          return original.docTemplateType;
        },
      },
      {
        Header: '',
        id: 'actions',
        width: 50,
        disableSortBy: true,
        Cell: ({ row }: { row: any }) => {
          const { id, team, isVirtualFolder, isFolder, name } = row.original;
          const isTemplateSelected = id === selectedTemplateId;

          if (isVirtualFolder || isFolder) {
            return (
              <div className="d-flex align-items-center justify-content-end">
                <Button
                  variant="link"
                  className="ml-2"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent double-firing with row click
                    onFolderClick && onFolderClick(id, name, isVirtualFolder);
                  }}
                  data-testid="lease-template-folder-open"
                >
                  <TitleFormattedMessage id="lease.template.cta.open" defaultMessage="Open" />
                  <ChevronRightIcon className="ml-2" />
                </Button>
              </div>
            );
          }

          return (
            <div className="d-flex align-items-center justify-content-end">
              <Button
                variant="link"
                className={classNames(styles.selectedTemplateButton, 'ml-2', {
                  'text-muted font-weight-bold': isTemplateSelected,
                })}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent double-firing with row click
                  selectTemplate(id, team?.uuid, team?.name);
                }}
                data-testid="lease-template-select"
              >
                <TitleFormattedMessage
                  id="lease.template.cta.select"
                  defaultMessage={isTemplateSelected ? 'Selected' : 'Select'}
                />
                {!isTemplateSelected && <ChevronRightIcon className="ml-2" />}
              </Button>
            </div>
          );
        },
      },
    ],
    [selectTemplate, selectedTemplateId]
  );

  const table = useTable(
    {
      data: dataWithClickHandlers,
      columns,
      initialState: {
        sortBy: [
          {
            id: 'name',
            desc: false,
          },
        ],
      },
    },
    useSortBy
  );

  return (
    <div className={styles.modalTableContainer}>
      {/* Breadcrumb Navigation */}
      {currentPath && currentPath.length > 1 && (
        <div className="d-flex align-items-center mb-2" style={{ fontWeight: 'bold', fontSize: '14px' }}>
          <HomeIcon width={18} height={18} className="text-primary mr-2" style={{ marginTop: '-1px' }} />
          {currentPath.map((pathItem, index) => (
            <React.Fragment key={pathItem.id || 'root'}>
              {index === currentPath.length - 1 ? (
                <span className="text-dark">{pathItem.name}</span>
              ) : (
                <>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none text-primary"
                    style={{ fontWeight: 'bold', fontSize: '14px' }}
                    onClick={onNavigateBack}
                  >
                    {pathItem.name}
                  </Button>
                  <span className="mx-2 text-muted">{'>'}</span>
                </>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      <Table striped table={table} isStickyHeader />
    </div>
  );
};
